import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { api, errMsg } from "@/lib/api";
import { shown, visibleColumns } from "@/lib/theme";
import { InstagramStrip } from "@/components/InstagramStrip";

const FLink = ({ l, className, testId }: any) =>
  /^(https?:|mailto:|tel:)/.test(l.url || "") ? (
    <a href={l.url} target={l.url.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={className} data-testid={testId}>{l.label}</a>
  ) : (
    <Link to={l.url || "/"} className={className} data-testid={testId}>{l.label}</Link>
  );

const slug = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Newsletter({ site }: any) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); setDone(true); setEmail(""); toast("Thank you for subscribing"); } catch (x) { toast.error(errMsg(x)); }
  };
  return (
    <div id="newsletter" className="grid md:grid-cols-[1fr_1.2fr_0.6fr] gap-8 md:gap-12 items-center py-14 md:py-20 scroll-mt-32" data-testid="footer-newsletter">
      <div>
        <h2 className="font-display uppercase text-3xl md:text-[2.6rem] tracking-[0.04em] leading-none">{site?.newsletter_heading || "Get the Latest"}</h2>
        <p className="mt-4 text-[15px] md:text-base leading-relaxed text-neutral-800 max-w-md">{site?.newsletter_text}</p>
      </div>
      <form onSubmit={submit} className="flex items-center rounded-full bg-[#F8F6F2] pl-7 pr-3 h-16 md:h-20 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)]">
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={done ? "SUBSCRIBED — THANK YOU" : "EMAIL"}
          className="flex-1 bg-transparent outline-none text-[15px] tracking-[0.08em] placeholder:text-neutral-900 uppercase" data-testid="newsletter-email-input" aria-label="Email address" />
        <button aria-label="Subscribe" className="h-11 w-11 grid place-items-center rounded-full hover:bg-white transition-colors" data-testid="newsletter-submit-button"><ArrowRight size={20} strokeWidth={1.4} /></button>
      </form>
      <div />
    </div>
  );
}

export function Footer() {
  const { site } = useSite();
  const year = String(new Date().getFullYear());
  return (
    <>
    <InstagramStrip />
    <footer className="bg-[var(--c-footer-bg)] text-[var(--c-footer-fg)] mt-24" data-testid="site-footer">
      <div className="max-w-[1500px] mx-auto px-6 md:px-16">
        <div className="flex justify-center pt-16 md:pt-20 pb-12 md:pb-16">
          <img src={site?.footer_logo_url || "/shield-somnio.png"} alt={site?.brand || "Somnio.Co"} className="h-12 md:h-[3.6rem] w-auto select-none" draggable={false} data-testid="footer-shield-logo" />
        </div>
        <nav className="grid grid-cols-2 md:grid-cols-3 lg:[grid-template-columns:repeat(var(--fc),minmax(0,1fr))] gap-x-8 gap-y-12" style={{ "--fc": Math.max(1, visibleColumns(site?.footer_menu).length) } as React.CSSProperties} data-testid="footer-columns">
          {visibleColumns(site?.footer_menu).map((col: any) => (
            <div key={col.title} data-testid={`footer-col-${slug(col.title)}`}>
              <p className="uppercase text-[15px] md:text-base font-medium tracking-[0.06em] mb-5">{col.title}</p>
              <ul className="space-y-2.5">
                {(col.links || []).map((l: any) => (
                  <li key={l.label + l.url}><FLink l={l} testId={`footer-link-${slug(l.label)}`} className="text-[14px] md:text-[15px] tracking-[0.09em] text-neutral-800 hover:text-[var(--c-primary)] transition-colors" /></li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <Newsletter site={site} />
      </div>
      <div className="max-w-[1500px] mx-auto px-6 md:px-16">
        <div className="border-t border-neutral-900 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4 text-[13px] md:text-[15px] tracking-[0.08em] uppercase">
          <span data-testid="footer-copyright">{(site?.copyright || "© {year} SOMNIO.CO P/L").replace("{year}", year)}</span>
          <div className="flex flex-wrap gap-x-8 gap-y-2" data-testid="footer-legal">
            {shown(site?.footer_legal).map((l: any) => <FLink key={l.label} l={l} testId={`footer-legal-${slug(l.label)}`} className="hover:text-[var(--c-primary)] transition-colors" />)}
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}
